import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import Logo from "@/components/Logo";
import {
  X, Home, Grid3x3, LayoutDashboard, CalendarDays, Shield, LogIn,
  UserPlus, LogOut, Globe, Check, UserCircle2, ClipboardList, Activity,
  Heart, Inbox, Settings as SettingsIcon, Wrench,
} from "lucide-react";

// WL-MOBILE-DRAWER-LEFT — Left-anchored drawer used by the public
// Navbar on small viewports (< md). Holds the destinations and CTAs
// that used to clutter the mobile header (Hub, Dashboard, Calendar,
// language picker, login / register / logout) so the bar itself can
// stay minimal: hamburger left + logo centered.
//
// Why left-anchored: per José's spec the drawer must read as a
// "banner / side panel" anchored to the hamburger button on the left.
// The hamburger sits on the left edge of the header so the drawer
// sliding from the same side preserves the spatial relationship.
//
// Why rendered through a React portal: the public `<Navbar/>` lives
// inside a `<header>` that uses the `tsw-glass` utility (which sets
// `backdrop-filter`). `backdrop-filter` creates a CSS containing
// block for `position:fixed` descendants, so a fixed/inset-0 drawer
// rendered inside the header would be clipped to the header's
// 64px-tall sticky box instead of covering the full viewport.
// `createPortal(<drawer/>, document.body)` sidesteps that ancestor
// chain entirely.
//
// Why a custom drawer instead of the shadcn `Sheet`:
//  • The shadcn Sheet relies on Radix's `Dialog`, which traps focus and
//    body-scrolls aggressively; we already manage z-index conflicts
//    with the sticky AdminTopBar and language popup. A plain portal-less
//    overlay with ESC + backdrop click is enough here.
//  • Smaller bundle, no extra subcomponents.

const SHORT_LABEL = { en: "EN", es: "ES", zh: "中文", "pt-br": "PT-BR" };
const shortLabel = (code) => SHORT_LABEL[code] || code.toUpperCase();

const STAFF_ROLE_KEYS = new Set([
  "admin", "staff", "qa_manager", "qa_tester", "institutional_observer",
]);
const resolveIdentityRole = (user, t) => {
  if (!user) return "";
  if (STAFF_ROLE_KEYS.has(user.role)) {
    const tr = t(`roles.${user.role}`);
    if (tr && tr !== `roles.${user.role}`) return tr;
  }
  if (user.profile_key) {
    const tr = t(`profiles.${user.profile_key}`);
    if (tr && tr !== `profiles.${user.profile_key}`) return tr;
  }
  return user.role || "";
};

const MobileDrawer = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLangManual } = useLang();
  const navigate = useNavigate();

  // ESC to close + lock body scroll while the drawer is mounted.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const onLogout = () => { logout(); onClose(); navigate("/"); };
  const onNav = (to) => { onClose(); navigate(to); };
  const pickLang = (code) => { setLangManual(code); };

  const activeLangs = LANGUAGES.filter((l) => l.isActive);
  const identityRole = resolveIdentityRole(user, t);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] md:hidden"
      data-testid="mobile-drawer"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t("common.close") || "Close"}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        data-testid="mobile-drawer-backdrop"
      />
      {/* Panel — LEFT-anchored, 86vw / max 360px, full viewport height. */}
      <aside
        className="absolute top-0 left-0 h-full w-[86vw] max-w-[360px] bg-white shadow-2xl flex flex-col"
        data-testid="mobile-drawer-panel"
      >
        <div className="flex items-center justify-between gap-2 px-4 h-16 border-b border-slate-100">
          <Logo size={36} />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
            data-testid="mobile-drawer-close"
            aria-label={t("common.close") || "Close"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {/* Identity (logged-in only) */}
          {user && (
            <div
              className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2.5"
              data-testid="mobile-drawer-identity"
            >
              <UserCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="mobile-drawer-user-name">
                  {user.full_name || user.email}
                </div>
                {identityRole && (
                  <div className="text-[11px] text-emerald-700 truncate" data-testid="mobile-drawer-user-role">
                    {identityRole}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Primary nav */}
          <nav className="space-y-1" aria-label="primary">
            <NavLink
              to="/"
              end
              onClick={() => onNav("/")}
              className={({isActive}) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                }`
              }
              data-testid="mobile-drawer-home"
            >
              <Home className="w-4 h-4" /> {t("common.home")}
            </NavLink>
            <NavLink
              to="/apps"
              onClick={() => onNav("/apps")}
              className={({isActive}) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                }`
              }
              data-testid="mobile-drawer-hub"
            >
              <Grid3x3 className="w-4 h-4" /> {t("apps.heroBadge")}
            </NavLink>
            {/* Apps inside the Hub — surfaced one level deeper so a
                first-time mobile visitor can reach Questionnaires or
                Racket Lab in one tap from the drawer. Rendered slightly
                indented so the hierarchy reads clean. */}
            <NavLink
              to="/app"
              onClick={() => onNav("/app")}
              className={({isActive}) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 ms-4 text-[13px] font-medium transition ${
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
                }`
              }
              data-testid="mobile-drawer-app-questionnaires"
            >
              <ClipboardList className="w-3.5 h-3.5" /> TEAM Questionnaires
            </NavLink>
            {/* Phase A6-prod (Feb 2026) — Racket Lab queda admin-only en
                Producción. El backend ya bloquea con 403 vía
                HUB_REQUIRE_RACKET_LAB_MEMBERSHIP, pero ocultamos también
                el enlace para que la experiencia sea limpia: ningún
                usuario non-admin ve un acceso que no puede usar. */}
            {user && (user.role === "admin" || user.role === "staff") && (
              <NavLink
                to="/apps/racket-lab"
                onClick={() => onNav("/apps/racket-lab")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 ms-4 text-[13px] font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-app-racket-lab"
              >
                <Activity className="w-3.5 h-3.5" /> Racket Lab
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/app"
                onClick={() => onNav("/app")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" /> {t("common.dashboard")}
              </NavLink>
            )}
            {user && (
              user.profile_key === "player" ? (
                // RACKET-LAB-PLAYER-EQUIPMENT-UX-3 / Feb 2026 — Player
                // profile sees "Mis raquetas". Updated by
                // DASHBOARD-SIDEMENU-PROFILES-1-FIX1 to point at the
                // standalone /app/my-rackets page.
                <NavLink
                  to="/app/my-rackets"
                  onClick={() => onNav("/app/my-rackets")}
                  className={({isActive}) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                      isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid="mobile-drawer-my-rackets"
                >
                  <Wrench className="w-4 h-4" /> {lang === "en" ? "My rackets" : "Mis raquetas"}
                </NavLink>
              ) : (
                <NavLink
                  to="/app/calendar"
                  onClick={() => onNav("/app/calendar")}
                  className={({isActive}) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                      isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid="mobile-drawer-calendar"
                >
                  <CalendarDays className="w-4 h-4" /> {t("calendar.title")}
                </NavLink>
              )
            )}
            {user && (
              <NavLink
                to="/app/wishlists"
                onClick={() => onNav("/app/wishlists")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-wishlists"
              >
                <Heart className="w-4 h-4" /> {t("wishlist.navLabel") || t("wishlist.pageTitle") || "Wishlists"}
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/app/marketplace-requests"
                onClick={() => onNav("/app/marketplace-requests")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-marketplace"
              >
                <Inbox className="w-4 h-4" /> {t("marketplace.navLabel") || "Solicitudes"}
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/app/settings"
                onClick={() => onNav("/app/settings")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-settings"
              >
                <SettingsIcon className="w-4 h-4" /> {t("settings.navLabel")}
              </NavLink>
            )}
            {user && (user.role === "admin" || user.role === "staff") && (
              <NavLink
                to="/admin"
                onClick={() => onNav("/admin")}
                className={({isActive}) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid="mobile-drawer-admin"
              >
                <Shield className="w-4 h-4" /> {t("common.admin")}
              </NavLink>
            )}
          </nav>

          {/* Language */}
          <div data-testid="mobile-drawer-langs">
            <div className="flex items-center gap-2 mb-2 px-1 text-[11px] uppercase font-semibold tracking-wider text-slate-400">
              <Globe className="w-3.5 h-3.5" /> {t("common.language") === "common.language" ? "Language" : t("common.language")}
            </div>
            <ul className="grid grid-cols-2 gap-1.5">
              {activeLangs.map((opt) => {
                const selected = opt.code === lang;
                return (
                  <li key={opt.code}>
                    <button
                      type="button"
                      onClick={() => pickLang(opt.code)}
                      className={`w-full flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition ${
                        selected
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`mobile-drawer-lang-${opt.code}`}
                      aria-pressed={selected}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-[10px] text-slate-400 shrink-0">
                          {shortLabel(opt.code)}
                        </span>
                        <span className="truncate">{opt.nameNative}</span>
                      </span>
                      {selected && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* CTAs at the bottom */}
        <div className="border-t border-slate-100 p-4 space-y-2">
          {!user ? (
            <>
              <Link
                to="/register"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full rounded-full bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 transition"
                data-testid="mobile-drawer-register"
              >
                <UserPlus className="w-4 h-4" /> {t("common.register")}
              </Link>
              <Link
                to="/login"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                data-testid="mobile-drawer-login"
              >
                <LogIn className="w-4 h-4" /> {t("common.login")}
              </Link>
            </>
          ) : (
            <button
              type="button"
              onClick={onLogout}
              className="flex items-center justify-center gap-2 w-full rounded-full border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-50 transition"
              data-testid="mobile-drawer-logout"
            >
              <LogOut className="w-4 h-4" /> {t("common.logout")}
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default MobileDrawer;
